import type { TicketType } from '@/types';
import { DEFAULT_INSTANCE_INDEX } from '@/lib/ticket-courses';

/**
 * Validates ticket type and per-ticket meal selections for a single-guest flow.
 * Matches RSVPPage.submitRsvp (non-interest, non-apology path).
 *
 * @returns Error message for showError, or null if valid
 */
export function validateSingleGuestTicketSelections(
    availableTickets: TicketType[],
    selectedTicketId: string,
    ticketCourseSelections: Record<string, string>
): string | null {
    if (availableTickets.length === 0) {
        return null;
    }
    if (!selectedTicketId) {
        return 'Please select a ticket type';
    }

    const selectedTicket = availableTickets.find((t) => t.id === selectedTicketId);
    if (selectedTicket?.courses && selectedTicket.courses.length > 0) {
        for (const course of selectedTicket.courses) {
            const chosen = ticketCourseSelections[course.id];
            if (!chosen) {
                return 'Please select options for all courses';
            }
        }
    }

    return null;
}

/**
 * Writes responses.Tickets and responses.TicketCourses for one guest (qty 1, instance index 1).
 * Same structure as RSVPPage.submitRsvp when creating a new RSVP.
 */
export function mergeSingleGuestTicketIntoResponses(
    responses: Record<string, any>,
    availableTickets: TicketType[],
    selectedTicketId: string,
    ticketCourseSelections: Record<string, string>
): void {
    if (availableTickets.length === 0 || !selectedTicketId) {
        return;
    }

    const selectedTicket = availableTickets.find((t) => t.id === selectedTicketId);
    if (!selectedTicket) {
        return;
    }

    responses.Tickets = [
        {
            id: selectedTicket.id,
            name: selectedTicket.name,
            price: selectedTicket.price,
            qty: 1,
        },
    ];

    if (selectedTicket.courses && selectedTicket.courses.length > 0) {
        const courseData: Record<string, Record<number, Record<string, string>>> = {};
        courseData[selectedTicket.id] = { [DEFAULT_INSTANCE_INDEX]: {} };
        selectedTicket.courses.forEach((course) => {
            const selection = ticketCourseSelections[course.id];
            const trimmedSelection = selection ? selection.trim() : '';
            if (trimmedSelection) {
                courseData[selectedTicket.id][DEFAULT_INSTANCE_INDEX][course.id] = trimmedSelection;
            }
        });
        responses.TicketCourses = courseData;
    }
}
